import { appendQueryParams, convertToPluralIfNeeded, downloadAlbumWithMediaToQueueItem, getInitialsFromName, State, streamMediaWithAlbumToQueueItem, useHubSwitch, usePlayerState, useViewport } from '@bpm-web-app/utils';
import classNames from 'classnames';
import { useCallback, useContext, useMemo } from 'react';
import { Media } from '@bpm-web-app/api-client';
import { ArtistWithMediaCount, MediaWithAlbum } from '@bpm-web-app/stream-api-sdk';
import { Artist, ArtistWithAlbumCount, Album as DownloadAlbum } from '@bpm-web-app/download-api-sdk';
import { useDrag } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
import { AppLink } from '../app-link/app-link';
import PlayIcon from '../../../assets/icons/player-play-alt.svg';
import PauseIcon from '../../../assets/icons/player-pause-alt.svg';
import styles from './artist-card-image.module.css';
import { usePlayer } from '../../player/player.context';
import { LibraryTabsContext } from '../../../../../utils/src/lib/library-tabs.context';
import { DragDropItem, DragResult } from '../../droppable/droppable';
import useFollowArtist from '../three-dots-sheet/useFollowArtist';

export interface ArtistCardFullImageProps {
    artist: Artist
    link?: string;
    isPlayable?: boolean
}

export function ArtistCardImage({ artist, isPlayable = true, link }: ArtistCardFullImageProps) {
    const playerState = usePlayerState();
    const { silenceWorkaround, setQueue, originalListDetails, onPause } = usePlayer();
    const { isDownload } = useHubSwitch();
    const { libraryProperty } = useContext(LibraryTabsContext);
    const { id, slug, name: title } = artist;

    const subTitle = useMemo(() => {
        return isDownload
            ? convertToPluralIfNeeded((artist as ArtistWithAlbumCount).album_count, 'Track', 'Tracks')
            : convertToPluralIfNeeded((artist as ArtistWithMediaCount).media_count, 'Track', 'Tracks');
    }, [isDownload, artist]);

    const actualLink = link || `/artist/${artist.slug}`;

    const isPlaying = useMemo(() => {
        if (playerState !== State.Playing) {
            return false;
        }
        return originalListDetails?.identifier === slug && originalListDetails?.resource === 'artist';
    }, [playerState, originalListDetails, slug]);

    const handlePlay = useCallback(async () => {
        silenceWorkaround();
        const { data } = isDownload ? await Media.searchAlbum(true, {
            artist: slug,
            library: libraryProperty,
            limit: 20
        }) : await Media.searchMedia({
            artist: slug,
            library: libraryProperty,
            limit: 20
        });
        if (!data) return;
        const queueMedia = isDownload
            ? (data as DownloadAlbum[]).map((album) => downloadAlbumWithMediaToQueueItem(album))
            : (data as MediaWithAlbum[]).map((media) => streamMediaWithAlbumToQueueItem(media));

        setQueue(queueMedia, 0, { identifier: slug, resource: 'artist' });
    }, [isDownload, setQueue, silenceWorkaround, slug, libraryProperty]);

    const { followArtist } = useFollowArtist(id);
    const { isMobile } = useViewport();

    // eslint-disable-next-line no-empty-pattern
    const [{ }, dragRef, connectDragPreview] = useDrag<DragDropItem, any, { opacity: number }>({
        item: { id, title },
        canDrag() {
            return !isMobile;
        },
        end: (draggedItem, monitor) => {
            if (monitor.didDrop()) {
                // eslint-disable-next-line @typescript-eslint/no-shadow
                const { target } = monitor.getDropResult() as DragResult;
                if (target === 'favorites') {
                    followArtist();
                }
            }
        },
        type: 'Artist'
    });

    connectDragPreview(getEmptyImage(), {
        // IE fallback: specify that we'd rather screenshot the node
        // when it already knows it's being dragged so we can hide it with CSS.
        captureDraggingState: true,
    });

    const imageUrl = appendQueryParams(artist.artwork_url, { key: 'dw', value: 128 });
    const imageUrl2x = appendQueryParams(artist.artwork_url, { key: 'dw', value: 128 * 2 });

    return (
        <div ref={dragRef} className={classNames(styles['artist-card-image'])}>
            <AppLink href={actualLink}>
                <a>
                    <div className={styles['artist-card-image__img-container']}>
                        <figure className={styles['artist-card-image__image']}>
                            {imageUrl && imageUrl2x && (
                                <img src={imageUrl} draggable={false} alt={title} srcSet={imageUrl2x ? `${imageUrl2x} 2x` : undefined} />
                            )}
                            {(!imageUrl || !imageUrl2x) && <div className={styles['artist-card-image__name-initials']}>{getInitialsFromName(title)}</div>}
                        </figure>

                        <div className={classNames(styles['artist-card-image__card-overlay'], {
                            [styles['artist-card-image__card-overlay--disabled']]: !isPlayable
                        })}>
                            {isPlayable &&
                                <button
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        if (isPlaying) {
                                            onPause();
                                        } else {
                                            handlePlay();
                                        }
                                    }}
                                    type="button"
                                    aria-label={isPlaying ? 'Pause' : 'Play'}>
                                    <div className={styles['artist-card-image-play']}>
                                        {isPlaying ? <PauseIcon /> : <PlayIcon />}
                                    </div>
                                </button>
                            }
                        </div>

                    </div>
                </a>
            </AppLink>
            <div className={styles['artist-card-image__text-container']}>
                <AppLink href={actualLink}>
                    <a className={styles['artist-card-image__title']}>{title}</a>
                </AppLink>
                <div className={styles['artist-card-image__subtitle']}>{subTitle}</div>
            </div>
        </div>
    );
}

export default ArtistCardImage;
